import { logStatus } from "../utils";
import { toGoLang, AsyncConstructors, convertGlueMonitorBoundsToFinsemble, Gate, getComponentState, setComponentState, removeComponentState, formGroup, fetchWindowOptions } from "../common/windowFunctions";
var WINDOWSTATEImpl;
(function (WINDOWSTATEImpl) {
    WINDOWSTATEImpl[WINDOWSTATEImpl["NORMAL"] = 0] = "NORMAL";
    WINDOWSTATEImpl[WINDOWSTATEImpl["MINIMIZED_WAS_NORMAL"] = 1] = "MINIMIZED_WAS_NORMAL";
    WINDOWSTATEImpl[WINDOWSTATEImpl["MAXIMIZED"] = 2] = "MAXIMIZED";
    WINDOWSTATEImpl[WINDOWSTATEImpl["MINIMIZED_WAS_MAXIMIZED"] = 3] = "MINIMIZED_WAS_MAXIMIZED";
})(WINDOWSTATEImpl || (WINDOWSTATEImpl = {}));
export class FinsembleEventImpl {
    constructor(params) {
        this.canceled = false;
        this.delayed = false;
        this.alreadyClosed = false;
        this.gdwin = params.gdwin;
        this.eventName = params.eventName;
    }
    async maybeCloseAnyway() {
        if (!this.delayed && !this.canceled && !this.alreadyClosed) {
            await this.gdwin.close();
        }
    }
    async done() {
        if (!this.canceled) {
            this.alreadyClosed = true;
            await this.gdwin.close();
        }
    }
    async cancel() {
        this.canceled = true;
    }
    async wait() {
        this.delayed = true;
    }
}
class FinsembleViewImpl {
    constructor(params) {
        this.win = params.win;
        this.id = params.id;
    }
    async remove(cb) {
        await this.win.removeBrowserView(this, cb);
    }
    async setBounds(bounds, cb) {
        await this.win.setViewBounds({ view: this, bounds }, cb);
    }
    async bringToFront(cb) {
        await this.win.bringViewToFront(this, cb);
    }
}
export class FinsembleWindowImpl {
    constructor(gluePromise, asyncConstructors) {
        this.asyncConstructors = asyncConstructors;
        this.windowOptions = {};
        this.gate = new Gate();
        this.eventHandlers = [];
        this.closeRequestedHandlers = [];
        this.wrap = FinsembleWindowImpl.getInstance;
        const pw = asyncConstructors.addConstructor();
        gluePromise.then(async (g) => {
            this.glue = g;
            const win = this.glue.windows?.my();
            if (win) {
                this.gdwin = win;
                this.windowName = this.gdwin?.context["#finsemble-windowName"] ?? this.gdwin?.id;
                this.name = this.windowName;
                this.windowOptions = await fetchWindowOptions(this.gdwin);
                const { data: isAlwaysOnTop } = await getComponentState({ field: "isAlwaysOnTop" }, this.gdwin);
                if (isAlwaysOnTop) {
                    await this.gdwin.setOnTop("always");
                }
                else {
                    if (this.gdwin.settings.onTop) {
                        await setComponentState({ field: "isAlwaysOnTop", value: this.gdwin.settings.onTop }, this.gdwin);
                    }
                }
            }
            else {
                if (globalThis.window) {
                    this.logStatus("constructor", "warn", "Constructing FinsembleWindow on incompatible window type.");
                }
            }
            asyncConstructors.finishConstructor(pw);
        });
    }
    logStatus(method, type, data) {
        logStatus(FinsembleWindowImpl.loggerClient, this.glue, {
            api: "FinsembleWindow",
            method, type, data
        });
    }
    static setStaticInstances(gluePromise, loggerClient) {
        FinsembleWindowImpl.glueInstance = gluePromise;
        FinsembleWindowImpl.loggerClient = loggerClient;
    }
    async getComponentState(params, cb) {
        this.logStatus("getComponentState", "start");
        await this.asyncConstructors.ready();
        const gdwin = params?.windowName ? this.glue.windows.findById(params.windowName) : this.gdwin;
        const result = await getComponentState(params, gdwin);
        this.logStatus("getComponentState", "return", result);
        cb?.(result.err, result.data);
        return result;
    }
    async setComponentState(params, cb) {
        return await this.gate.wait(async () => this.setComponentStateInternal(params, cb));
    }
    async setComponentStateInternal(params, cb) {
        this.logStatus("setComponentState", "start");
        await this.asyncConstructors.ready();
        const gdwin = params.windowName ? this.glue.windows.findById(params.windowName) : this.gdwin;
        const result = await setComponentState(params, gdwin);
        this.logStatus("setComponentState", "return");
        cb?.(result.err, result.data);
        return result;
    }
    async removeComponentState(params, cb) {
        this.logStatus("removeComponentState", "start");
        await this.asyncConstructors.ready();
        const gdwin = params.windowName ? this.glue.windows.findById(params.windowName) : this.gdwin;
        const result = await removeComponentState(params, gdwin);
        this.logStatus("removeComponentState", "return");
        cb?.(result.err, result.data);
        return result;
    }
    static async getInstance(params, cb) {
        FinsembleWindowImpl.loggerClient.debug("FinsembleWindowImpl.getInstance", "start");
        const asyncConstructors = new AsyncConstructors();
        const instance = new FinsembleWindowImpl(FinsembleWindowImpl.glueInstance, asyncConstructors);
        await asyncConstructors.ready();
        const name = params?.windowName ?? params?.name ?? params?.id;
        const findByFinsembleWindowName = () => instance.glue.windows.list().find((w) => w.context["#finsemble-windowName"] === name);
        const findByName = () => instance.glue.windows.list().find((w) => w.name === name);
        const gdwin = instance.glue.windows.findById(name) ?? findByName() ?? findByFinsembleWindowName();
        if (gdwin) {
            instance.gdwin = gdwin;
            instance.windowName = gdwin?.context["#finsemble-windowName"] ?? gdwin?.id;
            instance.name = instance.windowName;
            instance.windowOptions = await fetchWindowOptions(gdwin);
            cb?.(null, instance);
            FinsembleWindowImpl.loggerClient.debug("FinsembleWindowImpl.getInstance", "return");
            return { wrap: instance };
        }
        else {
            const error = `FinsembleWindow.getInstance(): Cannot find window instance for id ${name}`;
            cb?.(error);
            throw new Error(error);
        }
    }
    removeEventListener(eventName, handler, logWarning = false) {
        this.logStatus("removeEventListener", "start", eventName);
        if (eventName === "close-requested") {
            const entry = this.closeRequestedHandlers.find((e) => e.handler === handler);
            if (entry) {
                entry.active = false;
            }
        }
        else {
            const index = this.eventHandlers.findIndex((item) => item?.eventName === eventName && item?.handler === handler);
            this.eventHandlers[index].unsubscriber();
            delete this.eventHandlers[index];
        }
        this.logStatus("removeEventListener", "return");
    }
    addEventListener(eventName, handler) {
        this.logStatus("addEventListener", "start", eventName);
        switch (eventName) {
            case "maximized": {
                const unsubscriber = this.gdwin.onMaximized(handler);
                this.eventHandlers.push({ eventName, handler, unsubscriber });
                return unsubscriber;
            }
            case "minimized": {
                const unsubscriber = this.gdwin.onMinimized(handler);
                this.eventHandlers.push({ eventName, handler, unsubscriber });
                return unsubscriber;
            }
            case "restored": {
                const unsubscriber = this.gdwin.onNormal(handler);
                this.eventHandlers.push({ eventName, handler, unsubscriber });
                return unsubscriber;
            }
            case "blurred": {
                const unsubscriber = this.gdwin.onFocusChanged(() => {
                    if (!this.gdwin.isFocused) {
                        handler();
                    }
                });
                this.eventHandlers.push({ eventName, handler, unsubscriber });
                return unsubscriber;
            }
            case "focused": {
                const unsubscriber = this.gdwin.onFocusChanged(() => {
                    if (this.gdwin.isFocused) {
                        handler();
                    }
                });
                this.eventHandlers.push({ eventName, handler, unsubscriber });
                return unsubscriber;
            }
            case "shown": {
                const unsubscriber = this.gdwin.onVisibilityChanged(() => {
                    if (this.gdwin.isVisible) {
                        handler();
                    }
                });
                this.eventHandlers.push({ eventName, handler, unsubscriber });
                return unsubscriber;
            }
            case "hidden": {
                const unsubscriber = this.gdwin.onVisibilityChanged(() => {
                    if (!this.gdwin.isVisible) {
                        handler();
                    }
                });
                this.eventHandlers.push({ eventName, handler, unsubscriber });
                return unsubscriber;
            }
            case "title-changed": {
                const unsubscriber = this.gdwin.onTitleChanged(handler);
                this.eventHandlers.push({ eventName, handler, unsubscriber });
                return unsubscriber;
            }
            case "bounds-changed": {
                const unsubscriber = this.gdwin.onBoundsChanged(handler);
                this.eventHandlers.push({ eventName, handler, unsubscriber });
                return unsubscriber;
            }
            case "clicked": {
                const unsubscriber = this.gdwin.onFrameButtonClicked(handler);
                this.eventHandlers.push({ eventName, handler, unsubscriber });
                return unsubscriber;
            }
            case "created": {
                const unsubscriber = this.glue.windows.onWindowAdded(handler);
                this.eventHandlers.push({ eventName, handler, unsubscriber });
                return unsubscriber;
            }
            case "closed":
            case "close-complete": {
                const unsubscriber = this.gdwin.onClose(handler);
                this.eventHandlers.push({ eventName, handler, unsubscriber });
                return unsubscriber;
            }
            case "close-requested": {
                const entry = { handler, active: true };
                this.closeRequestedHandlers.push(entry);
                const handlerWrapper = async (prevent) => {
                    if (entry.active) {
                        prevent({ showDialog: false });
                        const e = new FinsembleEventImpl({ gdwin: this.gdwin, eventName });
                        await handler(e);
                        await e.maybeCloseAnyway();
                    }
                };
                this.gdwin.onClosing(handlerWrapper);
                return () => {
                    console.error("iocd does not support unsubscribing from onClosing() handler");
                };
            }
            case "component-state-changed":
            case "bounds-change-start":
            case "startedMoving":
            case "bounds-changing":
            case "bounds-change-request":
            case "system-bounds-changed":
            case "system-maximized":
            case "system-restored":
            case "crashed":
            case "system-closed":
            case "stoppedMoving":
            case "broughtToFront":
            case "hide-requested":
            case "maximize-requested":
            case "minimize-requested":
            case "parent-set":
            case "parent-unset":
            case "ready":
            case "restore-requested":
            case "show-requested":
            case "wrap-state-changed":
            case "bounds-change-end":
            case "alwaysOnTop":
            default:
                return () => { console.log(`Event ${eventName} is not implemented`); };
        }
    }
    removeListener(eventName, handler, logWarning = false) {
        this.logStatus("removeListener", "start", eventName);
        return this.removeEventListener(eventName, handler, logWarning);
    }
    addListener(eventName, handler) {
        return this.addEventListener(eventName, handler);
    }
    ejectFromGroup() {
        this.logStatus("ejectFromGroup", "start");
        console.log("ejectFromGroup is not implemented in the Finsemble polyfill");
    }
    formGroup() {
        this.logStatus("formGroup", "start");
        formGroup(this.glue, this.gdwin.id);
        this.logStatus("formGroup", "return");
    }
    async getOptions(cb) {
        this.logStatus("getOptions", "start");
        cb?.(null, this.windowOptions);
        return {
            data: this.windowOptions
        };
    }
    getMonitor(cb) {
        this.logStatus("getMonitor", "start");
        this.asyncConstructors.ready().then(() => {
            this.gdwin.getDisplay().then((glueMonitor) => {
                const result = this.convertGetMonitor(glueMonitor);
                cb(result);
            });
        });
        this.logStatus("getMonitor", "return");
    }
    convertGetMonitor(glueMonitor) {
        if (!glueMonitor?.workArea || !glueMonitor?.scaleFactor || !glueMonitor.bounds) {
            return;
        }
        const { availableRect, monitorRect } = convertGlueMonitorBoundsToFinsemble(glueMonitor);
        const displayNumber = Number(glueMonitor?.name?.replace(/\D/g, "")) - 1;
        return {
            name: `device${displayNumber}`,
            left: availableRect.left,
            top: availableRect.top,
            right: availableRect.right,
            bottom: availableRect.bottom,
            availableRect,
            monitorRect,
            bounds: {
                max: {
                    x: availableRect.right,
                    y: availableRect.bottom
                },
                min: {
                    x: availableRect.left,
                    y: availableRect.top
                }
            }
        };
    }
    setOpacity(params, callback) {
        this.logStatus("setOpacity", "start");
        console.log("setOpacity is not implemented in the Finsemble polyfill");
    }
    showNativeOpenDialog(params = {}, cb) {
        this.logStatus("showNativeOpenDialog", "start");
        console.log("showNativeOpenDialog is not implemented in the Finsemble polyfill");
    }
    async close(params = {}, cb) {
        this.logStatus("close", "start");
        await this.asyncConstructors.ready();
        const { err } = await toGoLang(() => this.gdwin.close(), cb);
        this.logStatus("close", "return");
        return { err };
    }
    showAt(params, callback) {
        this.logStatus("showAt", "start");
        this.asyncConstructors.ready().then(() => {
            if (!this.gdwin.isVisible) {
                this.gdwin.show();
            }
            this.gdwin.moveTo(params?.top, params?.left).then(() => {
                this.logStatus("showAt", "return");
                callback?.(null);
            });
        });
    }
    async show(params = {}, cb) {
        this.logStatus("show", "start");
        await this.asyncConstructors.ready();
        const { err } = await toGoLang(() => this.gdwin.show(), cb);
        this.logStatus("show", "return");
        return { err };
    }
    reload(params = {}, callback) {
        this.logStatus("reload", "start");
        this.asyncConstructors.ready().then(() => {
            this.gdwin.refresh(false).then(() => {
                this.logStatus("reload", "return");
                callback?.(null);
            });
        });
    }
    async hide(params = {}, cb) {
        this.logStatus("hide", "start");
        await this.asyncConstructors.ready();
        const { err } = await toGoLang(() => this.gdwin.hide(), cb);
        this.logStatus("hide", "return");
        return { err };
    }
    updateOptions(params, callback) {
        this.logStatus("updateOptions", "start");
        console.log("updateOptions is not implemented in the Finsemble polyfill");
    }
    async getBounds(cb, backwardCompatibilityCB) {
        this.logStatus("getBounds", "start");
        await this.asyncConstructors.ready();
        const callback = typeof cb === "function" ? cb : backwardCompatibilityCB;
        const { err, data: bounds } = await toGoLang(() => this.gdwin.getBounds());
        if (err) {
            this.logStatus("getBounds", "error", err);
        }
        callback?.(null, bounds);
        this.logStatus("getBounds", "return", bounds);
        return {
            err: null,
            data: bounds
        };
    }
    async setBounds(params = { bounds: {} }, cb) {
        this.logStatus("setBounds", "start");
        await this.asyncConstructors.ready();
        if (typeof params !== "function" && !params.bounds) {
            const oldParams = params;
            params = {
                bounds: oldParams,
            };
        }
        const { err } = await toGoLang(() => this.gdwin.moveResize(params.bounds), cb);
        this.logStatus("setBounds", "return");
        return { err };
    }
    async setViewBounds(params, callback) {
        this.logStatus("setViewBounds", "start");
        console.log("setViewBounds is not implemented in the Finsemble polyfill");
    }
    async bringViewToFront(view, callback) {
        this.logStatus("bringViewToFront", "start");
        console.log("bringViewToFront is not implemented in the Finsemble polyfill");
    }
    async removeBrowserView(view, callback) {
        this.logStatus("removeBrowserView", "start");
        console.log("removeBrowserView is not implemented in the Finsemble polyfill");
    }
    async addBrowserView(params, callback) {
        this.logStatus("addBrowserView", "start");
        console.log("addBrowserView is not implemented in the Finsemble polyfill");
        return null;
    }
    setShape(params, callback) {
        this.logStatus("setShape", "start");
        console.log("setShape is not implemented in the Finsemble polyfill");
    }
    isShowing(params = {}, callback) {
        this.logStatus("isShowing", "start");
        callback(null, this.gdwin.isVisible && this.gdwin.state !== "minimized");
        this.logStatus("isShowing", "return");
    }
    async isAlwaysOnTop(params = {}, callback) {
        this.logStatus("isAlwaysOnTop", "start");
        const { data: isAlwaysOnTop } = await this.getComponentState({ field: "isAlwaysOnTop" });
        callback?.(null, isAlwaysOnTop);
        this.logStatus("isAlwaysOnTop", "return");
        return isAlwaysOnTop;
    }
    async setAlwaysOnTop(params = { alwaysOnTop: true }, cb) {
        this.logStatus("setAlwaysOnTop", "start");
        await this.asyncConstructors.ready();
        this.setComponentState({ field: "isAlwaysOnTop", value: params.alwaysOnTop });
        const { err } = await toGoLang(() => this.gdwin.setOnTop(params.alwaysOnTop ? "always" : false), cb);
        this.logStatus("setAlwaysOnTop", "return");
        return { err };
    }
    async bringToFront(params = null, cb) {
        this.logStatus("bringToFront", "start");
        await this.asyncConstructors.ready();
        const { err } = await toGoLang(() => this.gdwin.show());
        const { err: err2 } = await toGoLang(() => this.gdwin.focus());
        cb?.(err ?? err2);
        this.logStatus("bringToFront", "return");
        return { err: err ?? err2 };
    }
    async focus(params = null, cb) {
        this.logStatus("focus", "start");
        await this.asyncConstructors.ready();
        const { err } = await toGoLang(() => this.gdwin.focus(), cb);
        this.logStatus("focus", "return");
        return { err };
    }
    async blur(params = null, cb) {
        this.logStatus("blur", "start");
        await this.asyncConstructors.ready();
        console.log("blur is not implemented in the Finsemble polyfill");
        return {
            err: null
        };
    }
    async restore(params = null, cb) {
        this.logStatus("restore", "start");
        await this.asyncConstructors.ready();
        const { err } = await toGoLang(() => this.gdwin.restore(), cb);
        this.logStatus("restore", "return");
        return { err };
    }
    async maximize(params = null, cb) {
        this.logStatus("maximize", "start");
        await this.asyncConstructors.ready();
        const { err } = await toGoLang(() => this.gdwin.maximize(), cb);
        this.logStatus("maximize", "return");
        return { err };
    }
    async minimize(params = null, cb) {
        this.logStatus("minimize", "start");
        await this.asyncConstructors.ready();
        const { err } = await toGoLang(() => this.gdwin.minimize(), cb);
        this.logStatus("minimize", "return");
        return { err };
    }
}
FinsembleWindowImpl.WINDOWSTATE = WINDOWSTATEImpl;
